import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import JobCard from "@/components/JobCard";
import { STATUS_CONFIG, CATEGORY_CONFIG, OPS_ROLES } from "@/lib/constants";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Package, PlusCircle, Search } from "lucide-react";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "assigned", label: "Assigned" },
  { key: "en_route", label: "En Route" },
  { key: "arrived", label: "Arrived" },
  { key: "delayed", label: "Delayed" },
  { key: "completed", label: "Completed" },
];

export default function JobsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState(null);
  const [filter, setFilter] = useState("all");
  const [q, setQ] = useState("");

  const load = () => {
    setJobs(null);
    const params = filter === "all" ? {} : { status_filter: filter };
    api.get("/deliveries", { params }).then(({ data }) => setJobs(data)).catch(() => setJobs([]));
  };

  useEffect(load, [filter]);

  const isOps = OPS_ROLES.includes(user.role);
  const filtered = (jobs || []).filter((j) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (
      j.customer_name?.toLowerCase().includes(s) ||
      j.address?.toLowerCase().includes(s) ||
      j.po_number?.toLowerCase().includes(s)
    );
  });

  return (
    <div className="mx-auto max-w-6xl">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-stark-ink sm:text-4xl">Jobs</h1>
          <p className="mt-1 text-sm text-stark-muted">All installs, deliveries & service calls.</p>
        </div>
        {isOps && (
          <Button data-testid="jobs-new-btn" onClick={() => navigate("/jobs/new")} className="h-11 rounded-xl bg-stark-terracotta font-bold text-white hover:bg-[#bd4f2c]">
            <PlusCircle className="mr-1.5 h-4 w-4" /> New Job
          </Button>
        )}
      </div>

      <div className="mt-6 flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-stark-muted" />
          <Input
            data-testid="jobs-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search customer, address, PO…"
            className="h-11 rounded-xl border-stark-border bg-white pl-10"
          />
        </div>
      </div>

      <div className="stark-scroll mt-4 flex gap-2 overflow-x-auto pb-1">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`jobs-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition-all ${
              filter === f.key ? "bg-stark-ink text-white" : "bg-white text-stark-muted hover:bg-stark-tint"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {!jobs ? (
          <div className="grid gap-3 md:grid-cols-2">
            {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="stark-card flex flex-col items-center justify-center py-20 text-center">
            <Package className="h-10 w-10 text-stark-border-strong" />
            <p className="mt-3 font-semibold text-stark-ink">No jobs found</p>
            <p className="text-sm text-stark-muted">Try a different filter or search.</p>
          </div>
        ) : (
          <div className="grid gap-3 md:grid-cols-2">
            {filtered.map((j, i) => <JobCard key={j.id} job={j} index={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
